import { useEffect, useRef, useState } from "react";
import { useRecoilValue } from "recoil";
import styled from "styled-components";

import Modal from "components/organisms/Modal";
import ModalDelete from "components/organisms/ModalDelete";
import Btn from "components/atoms/buttons";
import CheckBox from "components/atoms/input/CheckBox";
import Gallery from "components/layouts/Header/ModalPalette/Palette/Gallery";
import ModalInquireGuide from "components/layouts/Header/ModalPalette/Palette/ModalInquireGuide";
import PaletteListItem from "components/layouts/Header/ModalPalette/Palette/ListItem";
import ModalGuideMeasure from "components/layouts/Header/ModalPalette/Palette/ModalGuideMeasure";

import useProject from "apis/queries/project/useProject";
import useDeletePin from "apis/mutations/pin/useDeletePin";
import useInquiry from "apis/queries/useInquiry";
import useDownloadExcel from "apis/mutations/pin/useDownloadExcel";

import { InputChange } from "types/Form";
import PinIdInfo from "types/PinIdInfo";

import sceneIndexAtom from "stores/sceneIndex";
import authAtom from "stores/auth";

import { ReactComponent as IconList } from "assets/images/palette/list.svg";
import { ReactComponent as IconGallery } from "assets/images/palette/gallery.svg";

interface Props {
  onChangeTypeContact: () => void;
  onChangeTypeHistory: () => void;
  onChnagePinIdInfo: (value: PinIdInfo[]) => void;
  pinIdInfo: PinIdInfo[];
  projectId: number;
}

// 마이팔레트
const Palette = ({
  onChangeTypeContact,
  onChangeTypeHistory,
  pinIdInfo,
  onChnagePinIdInfo,
  projectId,
}: Props) => {
  const [view, setView] = useState("gallery");
  const auth = useRecoilValue(authAtom);
  const [checkPrevPinIdInfo, setCheckPrevPinIdInfo] = useState<PinIdInfo[]>([]);
  const [isNoMeasure, setIsNoMeasure] = useState(false);
  const sceneRef = useRef<HTMLDivElement>(null);
  const sceneIndex = useRecoilValue(sceneIndexAtom);

  // 신청내역 api => 신청내역 버튼 활성화에 사용
  const inquiries = useInquiry(projectId);

  // 씬의 핀 목록 api
  const { project } = useProject({ projectId });

  // 핀 삭제 api
  const deletePin = useDeletePin({
    projectId,
    option: {
      onSuccess: () => onChnagePinIdInfo([]),
    },
  });

  // 엑셀 다운로드 api
  const downloadExcel = useDownloadExcel({
    onSuccess: () => onChnagePinIdInfo([]),
  });

  // 현재 씬의 위치로 스크롤 이동하기
  useEffect(() => {
    const sceneElement = sceneRef.current?.getElementsByClassName(
      `scene${sceneIndex + 1}`
    );

    if (sceneElement) {
      try
      {
        //에러 때문에 수정
        sceneElement[0].scrollIntoView();
      } catch (e) {
        console.error(e);
      }
    }
  }, [view]);

  if (!project) return null;

  // 핀 배열 생성 => 샘플 신청 시 서버로 보내는 데이터 형식으로 배열 생성
  // api 함수 useCreateInquiry (POST inquiry Api 참고)
  const scenePinIdInfo = project.scenes
    .map(({ pins }) => pins)
    .flat()
    .map((pin) => {
      const squareMeasure =
        checkPrevPinIdInfo.find((p) => p.pinId === pin.id)?.squareMeasure || "";
      return { pinId: pin.id, squareMeasure };
    });

  // 핀이 체크 되었는 지
  const isChecked = pinIdInfo.length <= 0;

  //체크박스 전체 선택 상태 업데이트
  const handleChangeAll = (e: InputChange) => {
    if (e.target.checked) {
      onChnagePinIdInfo(scenePinIdInfo || []);
    } else {
      onChnagePinIdInfo([]);
    }
  };

  // 체크박스 씬별 선택 상태 업데이트
  const handleChangeScenePins = ({
    checked,
    idInfos,
  }: {
    checked: boolean;
    idInfos: PinIdInfo[];
  }) => {
    if (checked) {
      const _pinIdInfo = [...pinIdInfo].filter(
        (v) => !idInfos.find((info) => info.pinId === v.pinId)
      );

      const newIdInfos = [..._pinIdInfo, ...idInfos].map((v) => {
        const squareMeasure =
          checkPrevPinIdInfo.find((p) => p.pinId === v.pinId)?.squareMeasure ||
          "";
        return { pinId: v.pinId, squareMeasure };
      });

      onChnagePinIdInfo(newIdInfos);
    } else {
      const ids = idInfos.map((v) => v.pinId);
      const newIdInfos = pinIdInfo.filter((pin) => !ids.includes(pin.pinId));
      onChnagePinIdInfo(newIdInfos);
    }
  };

  // 체크박스 개별 선택 상태 업데이트
  const handleChangeEachPin = ({
    checked,
    pinId,
  }: {
    checked: boolean;
    pinId: number;
  }) => {
    if (checked) {
      const squareMeasure =
        checkPrevPinIdInfo.find((p) => p.pinId === pinId)?.squareMeasure || "";
      const newPinIdInfo = [...pinIdInfo, { pinId, squareMeasure }];
      onChnagePinIdInfo(newPinIdInfo);
    } else {
      onChnagePinIdInfo(pinIdInfo.filter((info) => info.pinId !== pinId));
    }
  };

  // 면적 상태 업데이트
  const handleChageEachMeasure = ({
    pinId,
    squareMeasure,
  }: {
    pinId: number;
    squareMeasure: string;
  }) => {
    const isCheckedPin = pinIdInfo.find((p) => p.pinId === pinId);
    const isCheckedPrev = checkPrevPinIdInfo.find((p) => p.pinId === pinId);

    if (!isCheckedPrev) {
      const newIdInfos = checkPrevPinIdInfo.concat({ pinId, squareMeasure });
      setCheckPrevPinIdInfo(newIdInfos);
      return;
    }

    if (isCheckedPin) {
      //핀 선택 후 면적상태 업데이트
      const pinIdInfoIndex = pinIdInfo.findIndex((p) => p.pinId === pinId);
      const newPinInfoItem = [...pinIdInfo];

      newPinInfoItem[pinIdInfoIndex] = {
        ...newPinInfoItem[pinIdInfoIndex],
        squareMeasure,
      };

      onChnagePinIdInfo(newPinInfoItem);
    }

    //핀 선택 전 면적상태업데이트
    const checkPrevPinIndex = checkPrevPinIdInfo.findIndex(
      (p) => p.pinId === pinId
    );

    const newCheckPrevPin = [...checkPrevPinIdInfo];
    newCheckPrevPin[checkPrevPinIndex] = {
      ...newCheckPrevPin[checkPrevPinIndex],
      squareMeasure,
    };
    setCheckPrevPinIdInfo(newCheckPrevPin);
  };

  // 핀 삭제 이벤트
  const handleDeletePin = () => {
    if (isChecked) return;
    const pinIds = pinIdInfo.map((p) => p.pinId);
    deletePin(pinIds);
  };

  //엑셀 다운로드 이벤트
  const handleDownloadExcel = () => {
    if (isChecked) return;
    if (downloadExcel.isLoading) return;
    const pinIds = pinIdInfo.map((p) => p.pinId);
    downloadExcel.mutate({ ids: pinIds });
  };

  // 면적작성이 모두 되었는 지 체크
  const isFullMeasure = pinIdInfo.every((p) => !!p.squareMeasure);

  // 샘플신청하기 이벤트
  const handleApply = (onOpenModal: () => void) => {
    if (pinIdInfo.length === 0) return;

    if (auth.type !== "company") {
      onOpenModal();
      return;
    }

    if (!isFullMeasure) {
      setIsNoMeasure(true);
      onOpenModal();
      return;
    }

    setIsNoMeasure(false);
    onChangeTypeContact();
  };

  return (
    <>
      <div className="flex-none flex justify-between items-center my-5">
        <CheckBox
          checked={scenePinIdInfo.every(
            (p) => !!pinIdInfo.find((info) => p.pinId === info.pinId)
          )}
          onChange={handleChangeAll}
          children={
            <span className="font-bold">
              전체선택 ({scenePinIdInfo.length})
            </span>
          }
          className="gap-2"
        />

        <div className="flex gap-5 justify-end">
          <Btn.Blue
            onClick={handleDownloadExcel}
            size="small"
            inActive={isChecked || downloadExcel.isLoading}
            radiusFull
            width="80px"
          >
            {downloadExcel.isLoading ? "다운로드 중" : "다운로드"}
          </Btn.Blue>

          <Modal
            activator={(onOpen) => (
              <Btn.Blue
                onClick={() => handleApply(onOpen)}
                inActive={isChecked}
                size="small"
                radiusFull
                width="80px"
              >
                샘플 신청
              </Btn.Blue>
            )}
            modal={(onClose) => {
              if (auth.type !== "company") {
                return <ModalInquireGuide onClose={onClose} />;
              }
              return <ModalGuideMeasure onClose={onClose} />;
            }}
            backdropHidden
          />

          {auth.type === "company" && (
            <Btn.Blue
              onClick={onChangeTypeHistory}
              inActive={!inquiries || inquiries?.length <= 0}
              size="small"
              radiusFull
              width="80px"
            >
              신청내역
            </Btn.Blue>
          )}
          <Modal
            activator={(onOpen) => (
              <Btn.Red
                onClick={() => (!isChecked ? onOpen() : null)}
                inActive={isChecked}
                size="small"
                radiusFull
                width="80px"
              >
                선택삭제
              </Btn.Red>
            )}
            modal={(onClose) => (
              <ModalDelete onClose={onClose} onDelete={handleDeletePin} />
            )}
            backdropHidden
          />

          {view === "gallery" && (
            <button onClick={() => setView("list")}>
              <IconList />
            </button>
          )}
          {view === "list" && (
            <button onClick={() => setView("gallery")}>
              <IconGallery />
            </button>
          )}
        </div>
      </div>

      {view === "gallery" && (
        <GalleryContainer ref={sceneRef}>
          <Gallery
            scenes={project.scenes}
            projectId={projectId}
            pinIdInfo={pinIdInfo}
            isNoMeasure={isNoMeasure}
            onChangeEachPin={(params) => handleChangeEachPin(params)}
            onChangeScenePins={(params) => handleChangeScenePins(params)}
            onChageEachMeasure={(params) => handleChageEachMeasure(params)}
          />
        </GalleryContainer>
      )}
      {view === "list" && (
        <ListContainer ref={sceneRef}>
          {project.scenes.map((scene, i) => {
            return (
              <PaletteListItem
                key={scene.id}
                project={project}
                scene={scene}
                sceneIndex={i}
                pinIdInfo={pinIdInfo}
                isNoMeasure={isNoMeasure}
                onChangeEachPin={(params) => handleChangeEachPin(params)}
                onChangeScenePins={(params) => handleChangeScenePins(params)}
                onChageEachMeasure={(params) => handleChageEachMeasure(params)}
              />
            );
          })}
        </ListContainer>
      )}
    </>
  );
};

const ListContainer = styled.div`
  flex: 1;
  overflow-y: scroll;
  margin-right: -8px;
`;

const GalleryContainer = styled.div`
  flex: 1;
  overflow-y: scroll;
  overflow-x: hidden;
  margin-right: 16px;
  padding-right: 5px;
`;

export default Palette;
